import { useEffect } from "react";
import { createPortal } from "react-dom";
import "../../styles/dashboard/dashboard.css";

/*
  TransactionDetailOverlay
  ─────────────────────────
  Props:
    transaction — full transaction object (matches transactionSchema)
    currency    — string e.g. "₦" or "kr"
    onClose     — fn()
*/

const capFirst = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "—");
const fmtDate = (s) => (s ? new Date(s).toLocaleString("en", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const fmtAmt = (v, cur = "₦") => `${cur}${(v || 0).toLocaleString()}`;

const DELIVERY_STYLE = {
  supplied: "db-badge--supplied",
  pending: "db-badge--pending",
  notSupplied: "db-badge--notSupplied",
  returned: "db-badge--returned",
};
const PAYMENT_STYLE = {
  cash: "db-badge--cash",
  card: "db-badge--card",
  mobilePay: "db-badge--mobile",
  POS: "db-badge--cash",
  bankTransfer: "db-badge--bank",
};

export default function TransactionDetailOverlay({ transaction: t, currency, onClose }) {
  const d = t?.transactionDetails || {};
  const op = t?.operationType || {};
  const isLoan = op.whichOperation === "loan";

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  if (!t) return null;

  return createPortal(
    <div className='db-txn-overlay' onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className='db-txn-modal'>
        {/* Header */}
        <div className='db-txn-modal-head'>
          <div>
            <div className='db-txn-modal-transid'>{t.transID || "—"}</div>
            <div className='db-txn-modal-date'>{fmtDate(d.date || t.createdAt)}</div>
          </div>
          <button className='db-txn-xbtn' onClick={onClose} aria-label='Close'>
            <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round'>
              <line x1='18' y1='6' x2='6' y2='18' />
              <line x1='6' y1='6' x2='18' y2='18' />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className='db-txn-modal-body'>
          {/* Customer & Store */}
          <Section title='Customer & store'>
            <Grid>
              <Field label='Customer' value={t.customer?.fullName || "—"} />
              <Field label='Phone' value={t.customer?.phoneNumber || "—"} />
              <Field label='Email' value={t.customer?.email || "—"} small />
              <Field label='Address' value={t.customer?.address || "—"} small />
              <Field label='Store' value={t.store?.name || "—"} />
              <Field label='Sold by' value={t.soldBy?.fullName || "—"} />
            </Grid>
          </Section>

          {/* Payment & Delivery */}
          <Section title='Payment & delivery'>
            <Grid>
              <FieldBadge label='Payment mode' value={capFirst(d.paymentMode)} cls={PAYMENT_STYLE[d.paymentMode] || "db-badge--cash"} />
              <FieldBadge label='Operation type' value={isLoan ? "Loan" : "Cash"} cls={isLoan ? "db-badge--loan" : "db-badge--cash"} />
              <FieldBadge label='Delivery status' value={capFirst(d.deliveryStatus)} cls={DELIVERY_STYLE[d.deliveryStatus] || "db-badge--pending"} />
              <Field label='Items count' value={String(d.numberOfItems || 0)} />
            </Grid>
          </Section>

          {/* Loan details — only if loan */}
          {isLoan && (
            <Section title='Loan details'>
              <Grid>
                <Field label='Initial payment' value={fmtAmt(op.intialPayment, currency)} />
                <Field label='Balance' value={fmtAmt(op.balance, currency)} danger />
                <Field label='Loan date' value={fmtDate(op.loanDate)} small />
                <Field label='Due date' value={op.dateToPay || "—"} />
                {op.clearedAmount > 0 && <Field label='Cleared amount' value={fmtAmt(op.clearedAmount, currency)} />}
                {op.clearedDate && <Field label='Cleared date' value={op.clearedDate} small />}
              </Grid>
            </Section>
          )}

          {/* Products */}
          {t.products?.length > 0 && (
            <Section title='Products'>
              {t.products.map((p, i) => (
                <div key={i} className='db-txn-product'>
                  <div className='db-txn-product-img'>
                    {p.productImage ? (
                      <img
                        src={p.productImage}
                        alt={p.name}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <span style={{ fontSize: 18 }}>📦</span>
                    )}
                  </div>
                  <div className='db-txn-product-info'>
                    <div className='db-txn-product-name'>{p.name}</div>
                    <div className='db-txn-product-meta'>
                      {p.category} · {p.quantity} {p.unit} × {fmtAmt(p.soldUnitPrice, currency)}
                      {p.returned && (
                        <span className='db-badge db-badge--returned' style={{ marginLeft: 6 }}>
                          Returned
                        </span>
                      )}
                    </div>
                  </div>
                  <div className='db-txn-product-amt'>{fmtAmt(p.totalAmount, currency)}</div>
                </div>
              ))}
            </Section>
          )}

          {/* Amount summary */}
          <Section title='Amount summary'>
            <div className='db-txn-summary'>
              <SumRow label='Subtotal' value={fmtAmt(d.subTotal, currency)} />
              {d.discount > 0 && <SumRow label='Discount' value={`−${fmtAmt(d.discount, currency)}`} green />}
              {d.vat > 0 && (
                <SumRow
                  label={`VAT (${parseFloat(d.vatPercentage.$numberDecimal || d.vatPercentage.$numberDecimal || 0)}%)`}
                  value={`+${fmtAmt(d.vat, currency)}`}
                />
              )}
              {d.deliveryFee > 0 && <SumRow label='Delivery fee' value={`+${fmtAmt(d.deliveryFee, currency)}`} />}
              {d.serviceCharge > 0 && <SumRow label='Service charge' value={`+${fmtAmt(d.serviceCharge, currency)}`} />}
              {d.extraPayment > 0 && <SumRow label='Extra payment' value={`+${fmtAmt(d.extraPayment, currency)}`} />}
              {d.clearedbalance > 0 && <SumRow label='Cleared balance' value={`−${fmtAmt(d.clearedbalance, currency)}`} green />}
              <SumRow label='Total' value={fmtAmt(d.totalAmount, currency)} total />
            </div>
          </Section>

          {/* Comments */}
          {t.comments && (
            <Section title='Comments'>
              <div className='db-txn-comments'>{t.comments}</div>
            </Section>
          )}
        </div>

        {/* Footer */}
        <div className='db-txn-modal-foot'>
          <button className='db-txn-close-cta' onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ── Sub-components ── */
const Section = ({ title, children }) => (
  <div className='db-txn-section'>
    <div className='db-txn-section-title'>{title}</div>
    {children}
  </div>
);

const Grid = ({ children }) => <div className='db-txn-grid'>{children}</div>;

const Field = ({ label, value, small, danger }) => (
  <div className='db-txn-field'>
    <div className='db-txn-field-lbl'>{label}</div>
    <div className={`db-txn-field-val${small ? " small" : ""}${danger ? " danger" : ""}`}>{value}</div>
  </div>
);

const FieldBadge = ({ label, value, cls }) => (
  <div className='db-txn-field'>
    <div className='db-txn-field-lbl'>{label}</div>
    <div className='db-txn-field-val'>
      <span className={`db-badge ${cls}`}>{value}</span>
    </div>
  </div>
);

const SumRow = ({ label, value, total, green }) => (
  <div className={`db-txn-sum-row${total ? " total" : ""}`}>
    <span className='lbl'>{label}</span>
    <span className={`val${green ? " green" : ""}`}>{value}</span>
  </div>
);
